import {
  ActionManager,
  ExecuteCodeAction,
  Mesh,
  Nullable,
  PointerDragBehavior,
  Vector3,
} from '@babylonjs/core'
import { useDispatch } from 'react-redux'
import store, { AppDispatch } from '../../store'
import { useCallback, useEffect, useRef } from 'react'
import { isRoofMesh } from '../utils'
import { moveRoof } from '../../actions/construction'
import { updateHistory } from '../../actions/history'
import { ActionType } from '../../reducers/history'
import { MaterialManager } from '../materials/materialManager'
import { BuildingElementType } from '../../models/Construction'

interface EditRoofUtils {
  readonly changeRoofPosition: (value: Vector3) => void
  readonly saveChanges: () => void
  readonly resetChanges: () => void
}

export const useEditRoofTool = (
  roofMesh: Mesh,
  addAction: boolean = true,
): EditRoofUtils => {
  const dispatch = useDispatch<AppDispatch>()
  const roofPropsRef = useRef<{
    position: Vector3
  }>({
    position: new Vector3(),
  })

  const changeRoofPosition = useCallback(
    ({ x, y, z }: Vector3) => {
      if (!isRoofMesh(roofMesh)) {
        console.warn(`useEditRoofTool supports only meshes of 'Roof' type`, {
          mesh: roofMesh,
        })

        return
      }

      roofMesh.position.set(x, y, z)

      roofMesh.computeWorldMatrix(true)
    },
    [roofMesh],
  )

  const saveChanges = useCallback(() => {
    if (!isRoofMesh(roofMesh)) {
      console.warn(`useEditRoofTool supports only meshes of 'Roof' type`, {
        mesh: roofMesh,
      })

      return
    }

    const roofProps = roofPropsRef.current
    if (!roofProps.position.equalsWithEpsilon(roofMesh.position)) {
      dispatch(
        moveRoof({
          id: roofMesh.id,
          offset: {
            x: roofMesh.position.x - roofProps.position.x,
            // Need to swap between Y and Z, because backend uses Z axis as Up vector
            y: roofMesh.position.z - roofProps.position.z,
            z: roofMesh.position.y - roofProps.position.y,
          },
        }),
      )

      const convertData = store.getState().construction.convertData
      dispatch(
        updateHistory({
          convertData,
          items: [
            {
              itemId: roofMesh.id,
              actionType: ActionType.EDIT,
            },
          ],
        }),
      )

      roofProps.position.copyFrom(roofMesh.position)
    }
  }, [roofMesh, dispatch])

  const resetChanges = useCallback(() => {
    if (!isRoofMesh(roofMesh)) {
      console.warn(`useEditRoofTool supports only meshes of 'Roof' type`, {
        mesh: roofMesh,
      })

      return
    }

    const roofProps = roofPropsRef.current
    changeRoofPosition(roofProps.position)
  }, [roofMesh, changeRoofPosition])

  useEffect(() => {
    if (!isRoofMesh(roofMesh)) {
      console.warn(`useEditRoofTool supports only meshes of 'Roof' type`, {
        mesh: roofMesh,
      })

      return
    }

    const roofProps = roofPropsRef.current
    roofProps.position.copyFrom(roofMesh.position)

    let action: Nullable<ExecuteCodeAction> = null
    if (addAction) {
      const DRAG_ACTIVATION_DISTANCE = 2.5
      action = new ExecuteCodeAction(
        ActionManager.OnPickDownTrigger,
        ({ sourceEvent }) => {
          const isCtrlPressed = sourceEvent.ctrlKey
          if (isCtrlPressed) {
            return
          }

          let totalDragDistance = 0
          let timeoutHandler: NodeJS.Timeout
          let isDragDirectionLocked = false

          const startPoint = new Vector3()
          const dragDirection = new Vector3()
          const projectedXAxis = new Vector3()
          const projectedZAxis = new Vector3()
          const { x, y, z } = roofMesh.position
          const gridSettings = store.getState().construction.gridSettings
          const cellSize = gridSettings.snapToGrid ? gridSettings.cellSize : 1

          const pointerDragBehavior = new PointerDragBehavior({})
          pointerDragBehavior.useObjectOrientationForDragging = false
          pointerDragBehavior.moveAttached = false

          pointerDragBehavior.onDragStartObservable.add(
            ({ dragPlanePoint }) => {
              isDragDirectionLocked = false
              startPoint.copyFrom(dragPlanePoint)

              timeoutHandler = setTimeout(() => {
                pointerDragBehavior.moveAttached = true
                pointerDragBehavior.options.dragAxis = Vector3.UpReadOnly

                const materialManager = MaterialManager.getInstance()
                roofMesh.material = materialManager.getInactiveMaterial(
                  BuildingElementType.Roof,
                )
              }, 2000)
            },
          )

          pointerDragBehavior.onDragObservable.add(
            ({ dragDistance, dragPlaneNormal, dragPlanePoint }) => {
              clearTimeout(timeoutHandler)

              // Move alongside XZ plane
              if (!pointerDragBehavior.moveAttached) {
                if (isDragDirectionLocked) {
                  totalDragDistance += dragDistance
                  const newPosition = new Vector3(
                    x + dragDirection.x * totalDragDistance,
                    y + dragDirection.y * totalDragDistance,
                    z + dragDirection.z * totalDragDistance,
                  )

                  if (gridSettings.snapToGrid) {
                    newPosition.x =
                      Math.round(newPosition.x / cellSize) * cellSize
                    newPosition.z =
                      Math.round(newPosition.z / cellSize) * cellSize
                  }

                  changeRoofPosition(newPosition)
                } else {
                  const dragPlaneNormalLength = dragPlaneNormal.length()
                  const XAxisDot = Vector3.Dot(
                    Vector3.RightReadOnly,
                    dragPlaneNormal,
                  )
                  projectedXAxis.copyFrom(Vector3.RightReadOnly)
                  dragPlaneNormal.scaleAndAddToRef(
                    -XAxisDot / dragPlaneNormalLength,
                    projectedXAxis,
                  )

                  const wallNormalDot = Vector3.Dot(
                    Vector3.RightHandedForwardReadOnly,
                    dragPlaneNormal,
                  )
                  projectedZAxis.copyFrom(Vector3.RightHandedForwardReadOnly)
                  dragPlaneNormal.scaleAndAddToRef(
                    -wallNormalDot / dragPlaneNormalLength,
                    projectedZAxis,
                  )

                  dragPlanePoint.subtractToRef(startPoint, dragDirection)

                  const distanceX = Math.abs(
                    Vector3.Dot(projectedXAxis, dragDirection),
                  )
                  const distanceZ = Math.abs(
                    Vector3.Dot(projectedZAxis, dragDirection),
                  )
                  if (
                    dragDirection.length() > 0 &&
                    Math.max(distanceX, distanceZ) > DRAG_ACTIVATION_DISTANCE
                  ) {
                    isDragDirectionLocked = true
                    if (distanceX > distanceZ) {
                      dragDirection.copyFrom(Vector3.RightReadOnly)
                    } else {
                      dragDirection.copyFrom(Vector3.RightHandedForwardReadOnly)
                    }

                    pointerDragBehavior.options = {
                      dragAxis: dragDirection,
                    }
                  }
                }
              }
            },
          )

          pointerDragBehavior.onDragEndObservable.add(() => {
            roofMesh.removeBehavior(pointerDragBehavior)
            clearTimeout(timeoutHandler)

            const materialManager = MaterialManager.getInstance()
            roofMesh.material = materialManager.getNormalMaterial(
              BuildingElementType.Roof,
            )

            saveChanges()
          })

          roofMesh.addBehavior(pointerDragBehavior)
        },
      )

      roofMesh.actionManager?.registerAction(action)
    }

    return () => {
      if (action) {
        roofMesh.actionManager?.unregisterAction(action)
      }

      if (!roofProps.position.equalsWithEpsilon(roofMesh.position)) {
        resetChanges()
      }
    }
  }, [roofMesh, addAction, changeRoofPosition, saveChanges, resetChanges])

  return { changeRoofPosition, saveChanges, resetChanges }
}
